import { useEffect, useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import Brand from "../components/Brand";
import { useAuth } from "../context/AuthContext";
import {
  BookOpen, ArrowRight, CheckCircle2, ChevronDown, ChevronUp,
  Sparkles, Heart, Crown, ShieldCheck, Mail, Users, Layers,
  BarChart3, Globe, Calendar, GraduationCap, Star, Award, Zap,
  PenTool, DollarSign, Play, Mic, X, Pause, RotateCcw,
} from "lucide-react";

/* /business/affiliate-marketing-101
   Course #1 — "Beginner Affiliate Ministry: The Faitharama Playbook"

   Source brief: User uploaded a generic 10-chapter affiliate-marketing PDF
   Claude had written for them. Instruction: "Apply it to Faitharama.com."
   Every generic example (Amazon, ShareASale, WordPress, Bluehost, ConvertKit,
   ClickFunnels, etc.) is rewritten so it points back to Faitharama's own
   products + the 50% — flat for everyone commission engine. No outside tools
   are required — every step happens INSIDE the platform.

   ★ 7-DAY RULE (Mike, Feb 2026): Originally 10 chapters — collapsed to 7 to
   match the "EVERYTHING IS 7 DAYS · THAT'S GOD'S NUMBER" rhythm across the
   platform. Related chapters were merged (Affiliate Center + Mirror Site,
   Content + Traffic, Dashboard + Mistakes) so no content was lost.

   Audience: 60-80+ readers. Big type. Long-form readable.  Senior-friendly
   spacing. No emoji icons — Lucide only. Real Mike voice (warm + plain).  */

const G    = "#2E9F3A";
const O    = "#F39A1F";
const WINE = "#0F172A";
const NAVY = "#0F172A";

/* ───────── Course chapter content ─────────
   Each chapter is one expandable "lesson" with prose paragraphs, lists,
   and Faitharama-specific call-outs. */

const CHAPTERS = [
  {
    n: 1,
    title: "What Affiliate Marketing Really Is",
    summary: "Plain English: you recommend, your neighbor buys it, you get paid. No inventory. No shipping. No customer service.",
    body: [
      { type: "p", text: "Affiliate marketing is one of the simplest ways an ordinary person can make a little income from home. You share something you actually use and like. Someone clicks your link, buys it, and the company pays YOU a piece of that sale. That is it. There is no warehouse, no boxes on your kitchen table, no Stripe account to fight with, no angry customers calling at suppertime. Faitharama handles every penny of that for you." },
      { type: "h", text: "How the money actually moves" },
      { type: "p", text: "Three players. The Merchant (the company that made the book, supplement, or course — including Faitharama itself). The Affiliate (you). The Customer (your neighbor, your friend, your daughter-in-law)." },
      { type: "p", text: "The customer clicks YOUR Faitharama mirror link. They land on the product page. They check out. Stripe collects the money. Faitharama logs the sale to YOUR account. On payout day, your share lands in your bank. Done. You never touch a credit card." },
      { type: "h", text: "Our flat-rate promise (read this twice)" },
      { type: "list", items: [
        "Everyone earns a flat 50% of what Faitharama.com gets — recurring, forever.",
        "Trial members ($0) and paid members ($49 / $79 / $79) all earn the same flat 50% — recurring, forever.",
        "Digital products (books, courses): 50% of what Faitharama.com gets — flat, recurring, forever.",
        "Physical products (supplements, swag) — 50% of what Faitharama.com gets on every product, digital and physical.",
        "No tier ladders. No silver/gold/platinum nonsense. Just $49 or $79 — flat, simple, transparent.",
      ]},
      { type: "callout", text: "This is the part most platforms hide. We made it the headline. Mike does not believe in burying the commission rate." },
      { type: "h", text: "Why this works especially well for seniors" },
      { type: "p", text: "Recommend products you actually use. Word-of-mouth from a real customer beats any sales pitch — that's the whole reason affiliate marketing exists. You share the link, the customer buys through Faitharama, and the commission lands in your account. No script to memorize, no calls to make." },
    ],
  },
  {
    n: 2,
    title: "Picking Your Lane Inside Faitharama",
    summary: "You do not need to find a 'niche' on the open internet. Four lanes already exist inside the platform — pick the one that fits your life.",
    body: [
      { type: "p", text: "Most affiliate gurus will tell you to spend three weeks 'finding your niche.' We threw that step in the trash. Faitharama already has four built-in lanes, each with its own product line, its own buyer, and its own commission stream. Pick the one that fits you best." },
      { type: "h", text: "The four Faitharama lanes" },
      { type: "list-bold", items: [
        ["Wellness & Detox", "Lane around the With Faith All Things Are Possible book ($19) and the Goli + Piurify supplement family. Who it helps: anyone who has tried 'clean eating' and bounced off. Why it works: your honest experience matters more than any TikTok health video."],
        ["Ministry Tools (Builder)", "Lane around the $49 Pro tier — Funnelrama, mirror sites, AI Writer, email tools. Who it helps: small ministries, coaches, independent authors spending money on tools they don't use. Why it works: if it really did save you money, just say so."],
        ["Ministry Tools (Pro)", "Lane around the $79 Unlimited tier — Courses, Write A Book, unlimited everything. Who it helps: serious creators who have already tried ClickFunnels or Kajabi and quit. Why it works: if it covers what four other tools used to, just say that — plainly."],
        ["Books & Writing", "Lane around With Faith All Things Are Possible + Write A Book In 7 Days ($97) + AI Writer. Who it helps: future authors and devoted readers in your circle. Why it works: if you actually wrote one, share how — step by step."],
        ]},
      { type: "h", text: "The 60-second lane-picker" },
      { type: "list", items: [
        "Which Faitharama product do YOU already use almost every day? — that is your lane.",
        "Who in your real life keeps asking for that exact recommendation? — that is your audience.",
        "Could you write 30 short emails about this without running out of things to say? — yes? perfect. No? pick another lane.",
      ]},
      { type: "callout", text: "Tip from Mike: most members who quit picked a lane they don't actually use. Don't sell the Goli bottle if you've never opened one. Pick the product on your nightstand. That is the one you have a real story about." },
      { type: "h", text: "★ Honest-Promotion Rule (Read This Twice)" },
      { type: "p", text: "Faitharama's affiliate program has ONE rule that gets you banned for breaking it: whatever the partner brand says on their official product page — that is the most you can say. Nothing more. Nothing less. Let the product speak for itself." },
      { type: "list", items: [
        "DO say what the label says. 'Goli Apple Cider Vinegar Gummies — apple cider vinegar in a gummy.' That is from their site, you can repeat it.",
        "DO say your honest, plain experience. 'I take two every morning. I like them.'",
        "DO NOT add medical claims. 'Cures arthritis,' 'boosts immunity,' 'reverses aging' — those are illegal claims for supplements. The FTC will go after the brand AND you.",
        "DO NOT exaggerate income. 'I made $5,000 my first week' — unless you actually did and can prove it with a screenshot.",
        "DO disclose. Every Facebook post, email, blog: include one line — 'I earn a commission if you buy through this link.' Required by federal law.",
      ]},
      { type: "callout", text: "If you would not say it in front of a federal regulator, do not put it in your post. Stick to what the brand says — you keep your account, you keep your commissions, you keep your reputation." },
    ],
  },
  {
    n: 3,
    title: "Your Affiliate Center + Mirror Site",
    summary: "One login replaces six tools. Your dashboard already has every link you'll ever need — and your mirror site is already live before you finish coffee.",
    body: [
      { type: "p", text: "Generic affiliate guides tell beginners to apply to six different networks, learn six different dashboards, register a domain at GoDaddy, point DNS at Bluehost, install WordPress, pick a theme, install seven plugins, and hope it doesn't crash. We stripped that entire mountain out of your way. Every product you can sell is already inside your Faitharama Affiliate Center — and your public storefront is already built." },
      { type: "h", text: "Everything you can sell from day one" },
      { type: "list-bold", items: [
        ["With Faith All Things Are Possible", "50% of what Faitharama.com gets. Digital + paperback."],
        ["Funnelrama — included in your $49 plan", "50% RECURRING of what Faitharama.com gets. Every month they stay, you get paid every month."],
        ["Unlimited — $79/mo", "50% RECURRING of what Faitharama.com gets. Highest LTV product on the platform."],
        ["Write A Book In 7 Days", "50% of what Faitharama.com gets. Perfect fit for the Books & Writing lane."],
        ["Goli / Piurify supplements", "50% of what Faitharama.com gets (after partner cost). Mike vets every product personally."],
      ]},
      { type: "h", text: "What you already have, the day you sign up" },
      { type: "list-bold", items: [
        ["A public profile", "at faitharama.com/u/your-name — your photo, your story, your products. Mobile- and senior-friendly. No design skills required."],
        ["A mirror site", "at faitharama.com/m/your-handle — a clean, branded landing page that shows the Faitharama products YOU recommend. Every link automatically tagged with your affiliate code."],
        ["A storefront page", "at faitharama.com/u/your-name/store — a simple checkout-ready grid of every product you've turned on. Like an Amazon page, but every sale pays YOU."],
      ]},
      { type: "h", text: "Three places to share your mirror site this week" },
      { type: "list", items: [
        "Add it as a clickable link in your Facebook bio (under 'Intro').",
        "Print 50 simple business cards at Vistaprint with the URL on the back — hand them out at meetings, events, and to people who already know you. Cost: about $15.",
        "Put it as the signature on every email you already send (Gmail → Settings → Signature). Most members get their first commission this way without ever 'marketing.'",
      ]},
      { type: "callout", text: "Paid members can connect their own domain — YourMinistry.com — so the mirror site lives at YourMinistry.com instead of faitharama.com/m/your-handle. Helpful for established pastors and authors, but NOT a day-one task. Don't spend $14 on a domain before your first $14 commission. Earn it, then upgrade." },
    ],
  },
  {
    n: 4,
    title: "Content & Traffic That Quietly Sells",
    summary: "You're not running ads. You're telling stories. AI Writer drafts the words. Three quiet channels older buyers already use every day do the rest.",
    body: [
      { type: "p", text: "Forget 'content marketing' — that is a noisy word for what your grandmother already did: tell people what worked. The trick is doing it consistently and giving the link every single time. AI Writer (the AI helper inside your Faitharama dashboard) drafts the words for you. You review, fix, send." },
      { type: "h", text: "The five content shapes that quietly sell" },
      { type: "list-bold", items: [
        ["The 'Here's what I tried' review", "300-500 words. One product. One real story. One link at the bottom. Highest-converting shape on the platform."],
        ["The compare-two-things post", "300 words. 'Goli vs my old multivitamin.' 'With Faith vs the self-help shelf.' Comparison content converts because the reader is already shopping."],
        ["The 5-tip roundup", "'5 things every reader over 60 should know about [topic].' Five short blurbs, five links. One post, five chances to earn."],
        ["The how-to-fix-it post", "'How I stopped buying $40 supplements every month.' Problem → quick story → product link."],
        ["The habit-and-tool post", "'A daily habit + the tool that helps you stick with it.' Connect a real routine to a product that supports it. Plain and useful."],
      ]},
      { type: "h", text: "AI Writer in 60 seconds" },
      { type: "p", text: "Open AI Writer. Pick a shape ('Review post'). Type the product ('With Faith All Things Are Possible book'). Add three bullets about your experience. Hit Generate. You get a draft in 30 seconds. You read it out loud — does it sound like YOU? If not, edit two sentences. Done. Trial members get 3 generations/month. Pro ($49) members get up to 3 books/month. Unlimited ($79) members get 10 books/month. Unlimited ($79) members get UNLIMITED books." },
      { type: "h", text: "Three free traffic channels that work for seniors" },
      { type: "list-bold", items: [
        ["Facebook (still the giant for 55+)", "One post per day. Mirror-site link pinned to the top of your profile. Join 3-5 relevant groups (wellness, ministry, writing, etc.). Comment 5× for every 1 promotional post. Be a real human first."],
        ["Your email list", "Use the built-in Faitharama email tool — no Mailchimp, no ConvertKit, no extra bill. One email per week. Every email links to ONE thing. Email lists out-convert every other channel by 5x."],
        ["Search engines (Google + Pinterest)", "Write reviews on your Faitharama profile — these get indexed. Pinterest is still huge for 50-70 y/o women. Make 3 pins a week with Canva (free)."],
      ]},
      { type: "h", text: "The disclosure line nobody else mentions" },
      { type: "p", text: "Every post needs one honest line at the top: 'When you buy through my Faitharama link, I earn a small commission — at no extra cost to you.' That single sentence keeps you on the right side of FTC rules and, frankly, makes people MORE likely to click. Honesty sells." },
      { type: "callout", text: "Pick ONE channel for your first 60 days. Two channels means you get good at zero. Mike's advice: Facebook OR email, period — for the entire first 90 days." },
    ],
  },
  {
    n: 5,
    title: "Email Ministry — Where The Real Money Lives",
    summary: "Your list of 200 friends is worth more than 20,000 Instagram followers. The Faitharama email tool is built into every paid tier.",
    body: [
      { type: "p", text: "Every long-term Faitharama affiliate will tell you the same thing: the day I started taking my email list seriously is the day I started earning real money. Your list of 200 friends is worth more than someone else's list of 20,000 strangers." },
      { type: "h", text: "How to start your list this week (no extra tools)" },
      { type: "list", items: [
        "Open Funnelrama → New Funnel → pick the 'Free Gift in Exchange for Email' template.",
        "Pick a free magnet from Faitharama (e.g. the free Faitharama starter PDF, the Prayer Gift, a single Goli sample-sized PDF guide).",
        "Connect the funnel to your mirror site. Anyone who downloads becomes a subscriber on YOUR list — automatically.",
        "Faitharama hands you a welcome sequence (5 emails) free if you're on the $49 Builder tier. Pro gets the full automation tree.",
      ]},
      { type: "h", text: "The Mike Boyd email formula" },
      { type: "list-bold", items: [
        ["Subject line (under 7 words)", "Sound like a friend texting. 'I had to share this.' 'This one fixed my mornings.'"],
        ["Opening hook (2 lines)", "Personal — never marketing-speak."],
        ["The story (3-5 lines)", "What you tried, what it did, what changed."],
        ["The bridge (1 line)", "'And here's what surprised me.'"],
        ["The link (1 button, not 5)", "ONE call to action. Always ONE."],
        ["Signoff", "Your name. Your business or page name. Keep it personal — never a corporate tagline."],
      ]},
      { type: "h", text: "How often to send" },
      { type: "p", text: "Once a week. Same day every week (Tuesdays at 8 a.m. is Mike's slot). Predictable is profitable. People look forward to the Tuesday email — that is how trust builds." },
      { type: "callout", text: "Unlimited ($79) and Unlimited ($79) members unlock the Trip-Wire engine: someone downloads your free magnet → automatically gets offered a $7 With Faith chapter sampler PDF → if they buy, they get tagged 'Buyer' and dropped into a 14-email drip selling the $79/mo Unlimited plan. That single automation is why Pro pays for itself in one buyer." },
    ],
  },
  {
    n: 6,
    title: "Dashboard, Mistakes & The Mike-Boyd Method",
    summary: "Five numbers that matter. Six mistakes that kill beginners. A five-minute Friday review is the whole job.",
    body: [
      { type: "p", text: "You do not need Google Analytics. You do not need a spreadsheet. The Faitharama dashboard already shows you the only five numbers that matter — in plain English, with big type." },
      { type: "h", text: "The five numbers (and what 'good' looks like)" },
      { type: "list-bold", items: [
        ["Visitors this week", "How many real people saw your mirror site or funnel. Aim for 50+ in month 1, 200+ in month 3."],
        ["Clicks to product", "How many people clicked one of your product links. Aim for 5-10% of visitors."],
        ["Sales", "Pretty self-explanatory. One sale in your first month is a HUGE win. Most beginners give up at week 3 — the ones who push to week 8 win."],
        ["Commission this month", "Your actual paycheck so far. Updates the moment a sale clears Stripe."],
        ["Recurring subscribers", "How many Pro ($49) or Unlimited ($79) members you've referred who are still paying. This is your monthly residual — the most important number on the page."],
      ]},
      { type: "h", text: "The five-minute Friday habit" },
      { type: "list", items: [
        "Friday morning, coffee in hand, open the dashboard.",
        "Write down the five numbers.",
        "Ask: 'Which of my last 3 emails or posts earned anything?' — do MORE of that next week.",
        "Ask: 'Which one earned nothing?' — drop it.",
        "Close the laptop. That is the entire job.",
      ]},
      { type: "h", text: "The six mistakes that kill beginner affiliates" },
      { type: "list-bold", items: [
        ["Mistake 1 — Picking a lane they don't actually use", "If you don't open the With Faith All Things Are Possible book daily, don't try to sell it. Pick the product you LIVE."],
        ["Mistake 2 — Trying 5 channels at once", "Facebook + email + Pinterest + YouTube + TikTok in week 1 = zero results in week 12. Pick ONE for 90 days."],
        ["Mistake 3 — Quitting at week 3", "First commissions usually arrive between week 4 and week 9. The biggest predictor of success is simply staying past week 8."],
        ["Mistake 4 — Hiding the affiliate disclosure", "Not optional. Not a sales killer — it actually builds trust. Use it."],
        ["Mistake 5 — Not building an email list", "Social media is rented land. Your email list is the only asset YOU own. Start it in week 1."],
        ["Mistake 6 — Going wide instead of deep", "Selling 47 products = selling none. Pick 3 products you adore. Get great at THOSE three. Add a fourth in month 6, not month 1."],
      ]},
      { type: "callout", text: "Mike's rule: never check the dashboard daily. Daily checking creates anxiety and bad decisions. Weekly checking creates patterns and good decisions. The single biggest predictor of who wins: did they stay past week 8?" },
    ],
  },
  {
    n: 7,
    title: "Your 90-Day Faitharama Action Plan",
    summary: "Week by week. Idiot-proof. Print this page and tape it to the fridge.",
    body: [
      { type: "h", text: "Phase 1 — Foundation (Days 1-14)" },
      { type: "list", items: [
        "Day 1: Start your 7-day trial at Faitharama. Fill out your profile (photo + 3-line story + city).",
        "Day 2: Pick your lane (Chapter 2). Pick ONE.",
        "Day 3: Open your Affiliate Center. Bookmark it. Turn on 3 products you actually use.",
        "Day 4: Visit your mirror site (faitharama.com/m/your-handle). Take a screenshot of it.",
        "Day 5: Update your Facebook bio with the mirror-site URL.",
        "Day 6: Add the URL to your email signature in Gmail.",
        "Day 7: Send ONE message to ONE friend: 'I started something new — would you take a peek and tell me if the page makes sense?'",
        "Days 8-14: Order $15 of business cards from Vistaprint. Mirror-site URL on the back. Carry 5 with you every day.",
      ]},
      { type: "h", text: "Phase 2 — First Content (Days 15-30)" },
      { type: "list", items: [
        "Day 15: Open AI Writer. Draft your first 'Here's what I tried' review post. Publish it to Facebook.",
        "Day 17: Draft your first weekly email (5 sentences, 1 link). Send it.",
        "Days 18-25: One Facebook post every other day. One email every Tuesday. That is it.",
        "Day 30: Open dashboard. Write down your five numbers. Celebrate one win, no matter how small.",
      ]},
      { type: "h", text: "Phase 3 — Build the List (Days 31-60)" },
      { type: "list", items: [
        "Day 31: Open Funnelrama. Build the 'Free Gift for Email' funnel. Connect it to your mirror site.",
        "Day 35: Promote your free gift in one Facebook post AND one email.",
        "Days 36-60: Two Facebook posts per week. One email every Tuesday. Watch your subscribers grow.",
        "Day 60: Review dashboard. Did anyone subscribe? Did anyone click? Did anyone BUY? Most members get their first sale in this window.",
      ]},
      { type: "h", text: "Phase 4 — Compound (Days 61-90)" },
      { type: "list", items: [
        "Day 31: Decide whether to upgrade to Pro ($49/mo). Your 50% commission rate does NOT change — it's flat for everyone — but the paid tier unlocks unlimited contacts, unlimited emails, and the AI Writer.",
        "Day 70: Add a SECOND product to your weekly emails. Now you have variety.",
        "Days 70-90: Keep the rhythm. One post, one email per week.",
        "Day 90: Audit. Five numbers. Did anything change? Decide if you want to add a second channel in month 4 OR go deeper on your current one. The right answer is usually 'go deeper.'",
      ]},
      { type: "callout", text: "Print this list. Tape it to the fridge or stick it on your desk. Cross off each day. The members who PRINT this complete it. The members who don't, don't." },
    ],
  },
];

const INCOME_REALITY = [
  { stage: "Months 1-3 (Beginner)",        range: "starting income range",        note: "Most members earn their first earnings in month 2 or 3. If you have not earned by week 8, re-read Chapter 9." },
  { stage: "Months 4-12 (Building)",       range: "$300 – $3,000 / month",    note: "You have a small email list (50–500). You have a few real testimonials. Recurring commissions start stacking up." },
  { stage: "Year 2 (Established)",         range: "$3,000 – $15,000 / month", note: "You have a list of 1,000+. You have a Pro-tier subscriber base building. You write one email a week and that is the whole job." },
  { stage: "Year 3+ (Ministry Income)",    range: "$15,000+ / month",         note: "This is rare and it is earned. It almost always comes from Builder + Pro recurring referrals, not one-time product sales." },
];

const TOOLS = [
  { name: "AI Writer",          desc: "Built-in AI writing helper. Drafts reviews, emails, social posts in 30 seconds.",            tier: "Free preview · Builder & Pro full" },
  { name: "Funnelrama",            desc: "Drag-and-drop funnel + landing page builder with email capture built in.",                  tier: "Builder ($49) and up" },
  { name: "Email Tool",            desc: "Send weekly broadcasts. Welcome sequences. Trip-wire automations.",                         tier: "Builder = basic / Pro = full automation tree" },
  { name: "Affiliate Center",      desc: "One-click 'Copy My Link' for every Faitharama product. Auto-tags your handle.",             tier: "All tiers" },
  { name: "Mirror Site",           desc: "Your personal landing page at /m/your-handle. Every product pre-tagged with your code.",   tier: "All tiers" },
];

/* ───────── Page ───────── */

export default function AffiliateMarketing101Page({ embedded = false }) {
  const [open, setOpen] = useState(1);
  const [teleprompter, setTeleprompter] = useState(null);  /* chapter # or null */
  const { user } = useAuth();
  const isLoggedIn = !!user;

  /* Mike's recorded YouTube ID per chapter (set via /dashboard/lesson-videos).
     Keyed under the pseudo-slug "affiliate-101" so we share the same backend
     storage for course videos. */
  const [chapterVideos, setChapterVideos] = useState({});  // { "1": "ytId", ... }
  useEffect(() => {
    let cancelled = false;
    fetch("/api/lesson-videos", { cache: "no-store" })
      .then((r) => r.ok ? r.json() : { videos: {} })
      .then((j) => { if (!cancelled) setChapterVideos((j.videos || {})["affiliate-101"] || {}); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const youtubeForChapter = (n) => (chapterVideos[String(n)] || "").trim();

  /* When user uploads MP4 files later, we'll add per-chapter URLs to this map.
     For now every chapter shows the placeholder banner. Mike's workflow:
     audio + on-screen captions + B-roll images.  */
  const VIDEO_SRC = {};  /* e.g. { 1: "/videos/affiliate101/lesson-01.mp4" } */
  const COVER_IMG = "/courses/pro-affiliate-101.png";

  useEffect(() => {
    document.title = "Affiliate Marketing 101 — Beginner Ministry Playbook | Faitharama Courses";
    const metas = [
      { name: "description", content: "The Faitharama beginner affiliate marketing playbook. 7 chapters + 90-day action plan. Every example, niche, and tool points to Faitharama's built-in 50% — flat for everyone commission system. Senior-friendly, plain English." },
      { name: "keywords", content: "affiliate marketing for beginners, beginner affiliate marketing for seniors, Faitharama Courses, side income, beginner business course" },
      { property: "og:title",       content: "Affiliate Marketing 101 — The Faitharama Playbook" },
      { property: "og:description", content: "7-chapter beginner course. 90-day action plan. Built for the 60+ reader. Flat 50% commission, no tiers." },
      { property: "og:type",        content: "article" },
    ];
    const created = [];
    metas.forEach((m) => {
      const sel = m.name ? `meta[name="${m.name}"]` : `meta[property="${m.property}"]`;
      let tag = document.head.querySelector(sel);
      if (!tag) {
        tag = document.createElement("meta");
        if (m.name) tag.setAttribute("name", m.name);
        if (m.property) tag.setAttribute("property", m.property);
        document.head.appendChild(tag);
        created.push(tag);
      }
      tag.setAttribute("content", m.content);
    });
    return () => {
      created.forEach((el) => el.parentNode && el.parentNode.removeChild(el));
      document.title = "Faitharama";
    };
  }, []);

  const totalLessons = useMemo(() => CHAPTERS.length, []);

  return (
    <div className="min-h-screen" style={{ background: "#FFFFFF" }} data-testid="affiliate101-page">
      {!embedded && <SiteNav />}

      {/* ═══ HERO ═══ (light theme — Mike's strict palette: white + baby blue) */}
      <section className="relative overflow-hidden"
               style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #E0F2FE 60%, #FFFFFF 100%)" }}>
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-14 md:py-20">

          <div className="text-[10px] tracking-[0.32em] uppercase font-bold mb-3" style={{ color: "#C9202E" }}>
            ★ <Brand /> · COURSE 01
          </div>

          <h1 className="font-serif-display text-4xl sm:text-5xl md:text-7xl uppercase leading-[0.95]"
              style={{ color: "#0F172A", textWrap: "balance" }}>
            Affiliate Marketing 101.<br/>
            <span style={{ color: O }}>The Beginner Ministry Playbook.</span>
          </h1>
          <p className="mt-5 text-base md:text-2xl max-w-3xl leading-relaxed" style={{ color: "#0F172A" }}>
            7 chapters. A 90-day action plan. Written for the 60-and-up reader who has never sold anything online — and wants to share <strong style={{ color: "#C9202E" }}>real products</strong>, get paid <strong style={{ color: "#C9202E" }}>real commissions</strong>, and do it through one simple platform.
          </p>

          <div className="mt-7 grid sm:grid-cols-3 gap-3 max-w-3xl">
            {[
              { Icon: BookOpen,       k: `${totalLessons} chapters` , v: "Full text — read at your pace" },
              { Icon: Calendar,       k: "90-day plan",              v: "Print + tape to the fridge" },
              { Icon: GraduationCap,  k: "Included for everyone",          v: "Even trial members get full access" },
            ].map((c) => (
              <div key={c.k} className="rounded-xl px-4 py-3 flex items-center gap-3 bg-white shadow-sm"
                   style={{ border: `2px solid ${O}55` }}>
                <c.Icon className="w-5 h-5 flex-shrink-0" style={{ color: O }}/>
                <div>
                  <div className="text-sm font-bold" style={{ color: "#0F172A" }}>{c.k}</div>
                  <div className="text-[11px]" style={{ color: "#0F172A", opacity: 0.75 }}>{c.v}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            {isLoggedIn ? (
              <button
                onClick={() => {
                  document.getElementById("affiliate101-toc")?.scrollIntoView({ behavior: "smooth", block: "start" });
                  setOpen(1);
                }}
                data-testid="affiliate101-hero-cta-read"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-md text-base font-bold uppercase tracking-[0.18em] text-white shadow-lg hover:scale-[1.03] transition"
                style={{ background: G }}>
                <BookOpen className="w-5 h-5"/> Start Reading — Chapter 1 <ArrowRight className="w-5 h-5"/>
              </button>
            ) : (
              <Link to="/signup?plan=free" data-testid="affiliate101-hero-cta-free"
                    className="inline-flex items-center gap-2 px-7 py-4 rounded-md text-base font-bold uppercase tracking-[0.18em] text-white shadow-lg hover:scale-[1.03] transition"
                    style={{ background: G }}>
                <Sparkles className="w-5 h-5"/> Start 7-Day Free Trial — Earn 50% <ArrowRight className="w-5 h-5"/>
              </Link>
            )}
            {!isLoggedIn && (
              <Link to="/pricing" data-testid="affiliate101-hero-cta-paid"
                    className="inline-flex items-center gap-2 px-7 py-4 rounded-md text-base font-bold uppercase tracking-[0.18em] transition hover:scale-[1.03]"
                    style={{ background: "#FFFFFF", color: WINE, border: `2px solid ${O}` }}>
                <Crown className="w-5 h-5"/> Join Pro — $49/mo
              </Link>
            )}
          </div>
          {isLoggedIn && (
            <div className="mt-3 text-[11px] italic" style={{ color: "#0F172A", opacity: 0.75 }}>
              Signed in as <strong style={{ color: "#C9202E" }}>{user?.email || "you"}</strong> — you have full access. Scroll down to read.
            </div>
          )}
        </div>
      </section>

      {/* NO-LOGIN-NEEDED MEGA BANNER removed Feb 2026 — Mike's call: redundant with hero CTA. */}

      {/* ═══ COMMISSION BANNER ═══ */}
      <section className="border-y-2"
               style={{ background: "#EAF6FC", borderColor: `${O}55` }}
               data-testid="affiliate101-commission-banner">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-7 grid md:grid-cols-3 gap-4 items-center">
          <div>
            <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: WINE }}>★ THE WHOLE DEAL IN 2 LINES</div>
            <h3 className="font-serif-display text-2xl md:text-3xl leading-tight mt-1" style={{ color: WINE }}>
              Free = <span style={{ color: O }}>50%</span>. Paid = <span style={{ color: G }}>50%</span>.
            </h3>
            <p className="mt-2 leading-relaxed" style={{ color: "#1B1B1B", fontSize: "clamp(16px, 1.3vw, 19px)" }}>Flat rate. Recurring. Forever. No tiers. No hoops.</p>
          </div>
          <div className="md:col-span-2 grid sm:grid-cols-2 gap-3">
            <div className="rounded-xl p-4 bg-white shadow-sm" style={{ border: `2px solid ${O}55` }}>
              <div className="flex items-center gap-2"><DollarSign className="w-5 h-5" style={{ color: O }}/>
                <div className="font-bold" style={{ color: WINE, fontSize: "clamp(17px, 1.4vw, 20px)" }}>Digital products</div>
              </div>
              <p className="mt-1 leading-relaxed" style={{ color: "#1B1B1B", fontSize: "clamp(15px, 1.25vw, 18px)" }}>Books, courses, Funnelrama, Unlimited: <strong>50% of what Faitharama.com gets</strong> on every sale.</p>
            </div>
            <div className="rounded-xl p-4 bg-white shadow-sm" style={{ border: `2px solid ${O}55` }}>
              <div className="flex items-center gap-2"><DollarSign className="w-5 h-5" style={{ color: G }}/>
                <div className="font-bold" style={{ color: WINE, fontSize: "clamp(17px, 1.4vw, 20px)" }}>Physical products</div>
              </div>
              <p className="mt-1 leading-relaxed" style={{ color: "#1B1B1B", fontSize: "clamp(15px, 1.25vw, 18px)" }}>Supplements + swag: <strong>50% of what Faitharama.com gets</strong> after partner cost. Same flat rate as digital. Same fair split.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ TABLE OF CONTENTS ═══ */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 pt-12 pb-4" data-testid="affiliate101-toc">
        {/* Wide editorial banner — sets the tone before the chapter list */}
        <div className="mb-10 rounded-2xl overflow-hidden shadow-lg relative"
             style={{ border: `3px solid ${O}33`, aspectRatio: "16/9" }}
             data-testid="affiliate101-wide-banner">
          <img src="/courses/aff101-banner-wide.png"
               alt="A senior woman writing affiliate marketing notes at her desk, notebook and laptop beside her"
               className="absolute inset-0 w-full h-full object-cover"
               loading="lazy"/>
          <div className="absolute inset-0"
               style={{ background: "linear-gradient(90deg, rgba(11,11,11,0.0) 0%, rgba(11,11,11,0.0) 40%, rgba(11,11,11,0.55) 100%)" }}/>
          <div className="absolute inset-0 flex items-center justify-end px-6 md:px-10">
            <div className="text-right max-w-md">
              <div className="text-[10px] tracking-[0.3em] uppercase font-bold" style={{ color: O }}>
                ★ HOW IT FEELS WHEN YOU&apos;VE READ THE WHOLE THING
              </div>
              <h3 className="font-serif-display text-2xl md:text-4xl uppercase leading-[1.05] mt-1"
                  style={{ color: "#E0F2FE", textWrap: "balance", textShadow: "0 2px 12px rgba(0,0,0,0.55)" }}>
                One Email A Week.<br/>One Steady Habit.
              </h3>
              <p className="mt-2 text-sm md:text-base text-white/90 leading-relaxed">
                That&apos;s the whole job once you finish this playbook. Print it, tape it, walk it out.
              </p>
            </div>
          </div>
        </div>

        <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: O }}>★ TABLE OF CONTENTS</div>
        <h2 className="font-serif-display text-3xl md:text-5xl uppercase leading-[1.05] mt-2"
            style={{ color: WINE, textWrap: "balance" }}>
          Seven Chapters. One Pace.
        </h2>
        <p className="mt-3 max-w-3xl leading-relaxed" style={{ color: "#1B1B1B", fontSize: "clamp(18px, 1.55vw, 22px)" }}>
          Read one chapter a day with your morning coffee for a week. Or read the whole thing in an afternoon — whatever fits. Every chapter ends with a clear next step inside <Brand />.
        </p>

        <div className="mt-7 grid sm:grid-cols-2 lg:grid-cols-2 gap-3">
          {CHAPTERS.map((c) => (
            <a key={c.n}
               href={`#chapter-${c.n}`}
               onClick={() => setOpen(c.n)}
               data-testid={`affiliate101-toc-item-${c.n}`}
               className="rounded-xl bg-white p-4 shadow-sm hover:shadow-md transition flex items-start gap-3 border-2"
               style={{ borderColor: `${O}33` }}>
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                   style={{ background: `${O}1A`, color: O }}>
                <span className="font-serif-display text-2xl">{String(c.n).padStart(2, "0")}</span>
              </div>
              <div className="min-w-0">
                <div className="font-bold leading-tight" style={{ color: WINE, fontSize: "clamp(18px, 1.5vw, 22px)" }}>{c.title}</div>
                <p className="mt-1.5 leading-relaxed" style={{ color: "#1B1B1B", fontSize: "clamp(15px, 1.25vw, 18px)" }}>{c.summary}</p>
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* ═══ CHAPTERS (accordion) ═══ */}
      <section className="max-w-4xl mx-auto px-5 md:px-8 pb-14" data-testid="affiliate101-chapters">
        <div className="space-y-4">
          {CHAPTERS.map((c) => {
            const isOpen = open === c.n;
            return (
              <article key={c.n}
                       id={`chapter-${c.n}`}
                       className="rounded-2xl bg-white shadow-md overflow-hidden border-2"
                       style={{ borderColor: isOpen ? O : `${O}33` }}
                       data-testid={`affiliate101-chapter-${c.n}`}>
                <button onClick={() => setOpen(isOpen ? 0 : c.n)}
                        data-testid={`affiliate101-chapter-${c.n}-toggle`}
                        className="w-full text-left px-5 py-4 flex items-center justify-between gap-3 hover:bg-white/40 transition">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-14 h-14 md:w-16 md:h-16 rounded-lg flex items-center justify-center flex-shrink-0"
                         style={{ background: isOpen ? O : `${O}1A`, color: isOpen ? "#FFFFFF" : O }}>
                      <span className="font-serif-display text-2xl md:text-3xl">{String(c.n).padStart(2, "0")}</span>
                    </div>
                    <div className="min-w-0">
                      <div className="text-[12px] tracking-[0.24em] uppercase font-bold" style={{ color: O }}>CHAPTER {c.n}</div>
                      <h3 className="font-serif-display leading-tight" style={{ color: WINE, textWrap: "balance", fontSize: "clamp(22px, 2.4vw, 32px)" }}>
                        {c.title}
                      </h3>
                    </div>
                  </div>
                  {isOpen
                    ? <ChevronUp className="w-5 h-5 flex-shrink-0" style={{ color: O }}/>
                    : <ChevronDown className="w-5 h-5 flex-shrink-0 text-foreground/40"/>}
                </button>

                {isOpen && (
                  <div className="px-5 md:px-7 pb-7 pt-2 border-t" style={{ borderColor: `${O}22` }}
                       data-testid={`affiliate101-chapter-${c.n}-body`}>

                    {/* Video slot — embeds Mike's YouTube reading when set via
                        /dashboard/lesson-videos. Falls back to a "coming soon"
                        placeholder card otherwise. */}
                    <div className="mt-4 rounded-xl overflow-hidden relative bg-[#1B1B1B] aspect-video shadow-lg"
                         style={{ border: `2px solid ${O}66` }}
                         data-testid={`affiliate101-chapter-${c.n}-video-slot`}>
                      {youtubeForChapter(c.n) ? (
                        <iframe
                          title={`Affiliate 101 — Chapter ${c.n}`}
                          src={`https://www.youtube.com/embed/${youtubeForChapter(c.n)}?rel=0&modestbranding=1`}
                          className="absolute inset-0 w-full h-full"
                          frameBorder="0"
                          allow="autoplay; encrypted-media; picture-in-picture"
                          allowFullScreen
                        />
                      ) : (
                        <>
                          <img src={COVER_IMG} alt=""
                               className="absolute inset-0 w-full h-full object-cover opacity-35"/>
                          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/55 to-black/75"/>
                          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
                            <div className="w-14 h-14 rounded-full flex items-center justify-center mb-3"
                                 style={{ background: O, color: "#0F172A" }}>
                              <Play className="w-6 h-6" fill="#0F172A"/>
                            </div>
                            <div className="text-[10px] tracking-[0.3em] uppercase font-bold" style={{ color: "#E0F2FE" }}>
                              ★ LESSON {String(c.n).padStart(2, "0")} VIDEO
                            </div>
                            <div className="font-serif-display text-xl md:text-2xl text-white mt-1 leading-tight"
                                 style={{ textWrap: "balance" }}>
                              Coming soon — Mike is recording.
                            </div>
                            <div className="text-xs md:text-sm text-white/70 mt-2 max-w-sm leading-relaxed">
                              Read the full script below. Audio + captions + B-roll drop in here once recorded.
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Teleprompter button row */}
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <button onClick={() => setTeleprompter(c.n)}
                              data-testid={`affiliate101-chapter-${c.n}-teleprompter`}
                              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md text-[11px] tracking-[0.18em] uppercase font-bold transition hover:scale-[1.03]"
                              style={{ background: WINE, color: "#E0F2FE", border: `1.5px solid ${O}` }}>
                        <Mic className="w-3.5 h-3.5"/> Read Aloud Mode
                      </button>
                      <span className="text-[11px] text-foreground/60 italic">
                        Big print + auto-scroll — ready to record straight off the screen.
                      </span>
                    </div>

                    <div className="space-y-5 mt-5"
                         style={{ color: "#1B1B1B", fontSize: "clamp(20px, 1.85vw, 25px)", lineHeight: 1.55 }}>
                      {c.body.map((blk, i) => {
                        if (blk.type === "p") return <p key={i}>{blk.text}</p>;
                        if (blk.type === "h") return (
                          <h4 key={i} className="font-serif-display mt-7 mb-1 leading-[1.15]"
                              style={{ color: WINE, fontSize: "clamp(26px, 2.6vw, 36px)" }}>
                            ★ {blk.text}
                          </h4>
                        );
                        if (blk.type === "list") return (
                          <ul key={i} className="space-y-3 pl-1">
                            {blk.items.map((it, j) => (
                              <li key={j} className="flex items-start gap-3 leading-relaxed">
                                <CheckCircle2 className="w-6 h-6 flex-shrink-0 mt-1" style={{ color: G }}/>
                                <span>{it}</span>
                              </li>
                            ))}
                          </ul>
                        );
                        if (blk.type === "list-bold") return (
                          <ul key={i} className="space-y-3.5 pl-1">
                            {blk.items.map(([bold, rest], j) => (
                              <li key={j} className="flex items-start gap-3 leading-relaxed">
                                <Star className="w-6 h-6 flex-shrink-0 mt-1.5" style={{ color: O }}/>
                                <span><strong style={{ color: WINE }}>{bold} —</strong> {rest}</span>
                              </li>
                            ))}
                          </ul>
                        );
                        if (blk.type === "callout") return (
                          <div key={i}
                               className="rounded-xl px-5 py-4 my-4 flex items-start gap-3"
                               style={{ background: "#EAF6FC", border: `2px dashed ${O}88` }}>
                            <Heart className="w-6 h-6 flex-shrink-0 mt-1" style={{ color: O }}/>
                            <div className="italic leading-relaxed"
                                 style={{ color: "#1B1B1B", fontSize: "clamp(18px, 1.6vw, 22px)" }}>
                              {blk.text}
                            </div>
                          </div>
                        );
                        return null;
                      })}
                    </div>

                    {c.n < CHAPTERS.length && (
                      <button onClick={() => {
                                setOpen(c.n + 1);
                                requestAnimationFrame(() => {
                                  document.getElementById(`chapter-${c.n + 1}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
                                });
                              }}
                              data-testid={`affiliate101-chapter-${c.n}-next`}
                              className="mt-6 inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase font-bold hover:gap-2.5 transition"
                              style={{ color: WINE }}>
                        Chapter {c.n + 1} <ArrowRight className="w-3.5 h-3.5"/>
                      </button>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </section>

      {/* ═══ INCOME REALITY CHECK ═══ */}
      <section className="py-14" style={{ background: "#FFFFFF" }} data-testid="affiliate101-income">
        <div className="max-w-5xl mx-auto px-5 md:px-8">
          <div className="text-[10px] tracking-[0.32em] uppercase font-bold mb-2" style={{ color: O }}>★ THE HONEST INCOME REALITY CHECK</div>
          <h2 className="font-serif-display text-3xl md:text-5xl uppercase leading-[1.05]"
              style={{ color: "#FFFFFF", textWrap: "balance" }}>
            We Won&apos;t Lie To You.
          </h2>
          <p className="mt-3 max-w-3xl leading-relaxed text-white/85" style={{ fontSize: "clamp(18px, 1.55vw, 23px)" }}>
            Nobody earns $10,000 in their first 30 days. Don&apos;t believe the gurus who promise that. Here is what real Faitharama members actually earn at each stage of the journey:
          </p>

          <div className="mt-7 space-y-3">
            {INCOME_REALITY.map((row) => (
              <div key={row.stage}
                   className="rounded-xl p-5 grid md:grid-cols-3 gap-3 items-center"
                   style={{ background: "rgba(255,253,247,0.06)", border: `1.5px solid ${O}55` }}>
                <div className="text-[12px] tracking-[0.2em] uppercase font-bold" style={{ color: "#E0F2FE" }}>{row.stage}</div>
                <div className="font-serif-display" style={{ color: O, fontSize: "clamp(22px, 2.1vw, 30px)" }}>{row.range}</div>
                <div className="leading-relaxed text-white/85" style={{ fontSize: "clamp(15px, 1.3vw, 19px)" }}>{row.note}</div>
              </div>
            ))}
          </div>

          <div className="mt-7 rounded-xl px-5 py-4 italic text-sm md:text-base"
               style={{ background: "rgba(243,154,31,0.12)", color: "#E0F2FE", border: `1.5px solid ${O}55` }}>
            These ranges are observed across the Faitharama platform — not promises. Your income depends on your effort, your audience, and your consistency. We are not a get-rich scheme. We are a slow, steady, recurring income stream that compounds with consistency.
          </div>
        </div>
      </section>

      {/* ═══ TOOLS RECAP ═══ */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-14" data-testid="affiliate101-tools">
        <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: O }}>★ EVERYTHING YOU NEED — ALREADY INSIDE</div>
        <h2 className="font-serif-display text-3xl md:text-5xl uppercase leading-[1.05] mt-2"
            style={{ color: WINE, textWrap: "balance" }}>
          Six Tools. One Login. No Extra Bills.
        </h2>
        <p className="mt-3 max-w-3xl leading-relaxed" style={{ color: "#1B1B1B", fontSize: "clamp(18px, 1.55vw, 22px)" }}>
          Generic affiliate guides will tell you to subscribe to ConvertKit, then Bluehost, then SurferSEO, then ClickFunnels. We bundled the only six tools you actually need into Faitharama — flat rate, one login.
        </p>

        <div className="mt-7 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {TOOLS.map((t) => (
            <div key={t.name} className="rounded-2xl bg-white p-5 shadow-md border-2"
                 style={{ borderColor: `${O}33` }}>
              <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-3"
                   style={{ background: `${O}18`, color: O }}>
                <Zap className="w-5 h-5"/>
              </div>
              <h3 className="font-serif-display leading-tight" style={{ color: WINE, fontSize: "clamp(22px, 2vw, 28px)" }}>{t.name}</h3>
              <p className="mt-2 leading-relaxed" style={{ color: "#1B1B1B", fontSize: "clamp(16px, 1.4vw, 20px)" }}>{t.desc}</p>
              <div className="mt-3 inline-block text-[10px] tracking-[0.2em] uppercase font-bold px-2 py-1 rounded"
                   style={{ background: `${G}1A`, color: G }}>
                {t.tier}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ FINAL CTA ═══ */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 pb-16">
        <div className="rounded-2xl p-7 md:p-10 text-center"
             style={{ background: `linear-gradient(135deg, ${WINE} 0%, #7C2A2A 100%)`, border: `3px solid ${O}` }}>
          <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: O }}>
            ★ ONE LAST WORD FROM MIKE
          </div>
          <h3 className="font-serif-display text-3xl md:text-5xl uppercase mt-2 leading-tight" style={{ color: "#E0F2FE" }}>
            The Best Time To Start<br/>Was Yesterday.<br/>The Second Best Is Now.
          </h3>
          <p className="mt-3 max-w-2xl mx-auto text-base md:text-lg leading-relaxed text-white/85">
            You don&apos;t need experience. You don&apos;t need a website. You don&apos;t need a tech-savvy grandchild. You need a free account, one Faitharama product you already love, and 90 days of patient consistency. We&apos;ll handle the rest.
          </p>

          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <Link to="/signup?plan=free" data-testid="affiliate101-final-cta-free"
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-md text-base font-bold uppercase tracking-[0.18em] text-white shadow-lg hover:scale-[1.03] transition"
                  style={{ background: G }}>
              <Sparkles className="w-5 h-5"/> Take Your First Step — Free <ArrowRight className="w-5 h-5"/>
            </Link>
            <Link to="/pricing" data-testid="affiliate101-final-cta-pro"
                  className="inline-flex items-center gap-2 px-7 py-4 rounded-md text-base font-bold uppercase tracking-[0.18em] transition hover:scale-[1.03]"
                  style={{ background: "#FFFFFF", color: WINE, border: `2px solid #E0F2FE` }}>
              <Crown className="w-5 h-5"/> See All Plans — $0 to $79/mo
            </Link>
          </div>

          <div className="mt-6 grid sm:grid-cols-3 gap-3 max-w-2xl mx-auto">
            {[
              { Icon: ShieldCheck, t: "No credit card to start" },
              { Icon: Award,       t: "30-day Pro refund" },
              { Icon: Heart,       t: "Faith-built course" },
            ].map((x) => (
              <div key={x.t} className="flex items-center justify-center gap-2 text-xs text-white/75">
                <x.Icon className="w-4 h-4" style={{ color: O }}/> {x.t}
              </div>
            ))}
          </div>
        </div>

        <p className="mt-7 text-center text-[11px] italic text-foreground/55 px-3" style={{ textWrap: "balance" }}>
          This playbook is included in the <Brand /> Faitharama Courses and is provided as training material. Faitharama is not a financial advisor, and income examples are observed ranges — not guarantees. Your results depend on your effort, your audience, and your consistency.
        </p>
      </section>

      {!embedded && <SiteFooter />}

      {/* ═══ TELEPROMPTER MODAL ═══ */}
      {teleprompter !== null && (
        <TeleprompterOverlay
          chapter={CHAPTERS.find((c) => c.n === teleprompter)}
          onClose={() => setTeleprompter(null)}
        />
      )}
    </div>
  );
}

/* ───────── Teleprompter overlay ─────────
   Full-screen, cream-on-black, MASSIVE serif type, auto-scroll button.
   Mike records straight off the screen. Hit X to exit. */

function TeleprompterOverlay({ chapter, onClose }) {
  const containerRef = useRef(null);
  const [scrolling, setScrolling] = useState(false);
  const [speed, setSpeed] = useState(40);  /* px/sec */

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  useEffect(() => {
    if (!scrolling) return;
    let raf;
    let last = performance.now();
    const step = (t) => {
      const dt = (t - last) / 1000;
      last = t;
      if (containerRef.current) {
        containerRef.current.scrollTop += speed * dt;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [scrolling, speed]);

  if (!chapter) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex flex-col"
         style={{ background: "#0B0B0B" }}
         data-testid="affiliate101-teleprompter-overlay">

      {/* Top bar */}
      <div className="flex items-center justify-between px-4 md:px-6 py-3 border-b"
           style={{ background: "#1B1B1B", borderColor: "#333" }}>
        <div className="flex items-center gap-2 min-w-0">
          <Mic className="w-4 h-4" style={{ color: "#F39A1F" }}/>
          <div className="text-[10px] tracking-[0.24em] uppercase font-bold" style={{ color: "#E0F2FE" }}>
            ★ LESSON {String(chapter.n).padStart(2, "0")} · READ-ALOUD SCRIPT
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setScrolling((s) => !s)}
                  data-testid="teleprompter-toggle-scroll"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] tracking-[0.18em] uppercase font-bold transition hover:scale-[1.03]"
                  style={{ background: scrolling ? "#0F172A" : "#F39A1F", color: scrolling ? "#E0F2FE" : "#0B0B0B" }}>
            {scrolling ? <><Pause className="w-3.5 h-3.5"/> Pause</> : <><Play className="w-3.5 h-3.5"/> Auto-Scroll</>}
          </button>
          <button onClick={() => { if (containerRef.current) containerRef.current.scrollTop = 0; }}
                  data-testid="teleprompter-reset"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] tracking-[0.18em] uppercase font-bold transition hover:scale-[1.03]"
                  style={{ background: "transparent", color: "#E0F2FE", border: "1.5px solid #555" }}>
            <RotateCcw className="w-3.5 h-3.5"/> Top
          </button>
          <div className="hidden md:flex items-center gap-1 px-2 py-1.5 rounded-md"
               style={{ border: "1.5px solid #555" }}>
            <button onClick={() => setSpeed((s) => Math.max(15, s - 10))}
                    data-testid="teleprompter-slower"
                    className="text-[11px] font-bold px-1.5"
                    style={{ color: "#E0F2FE" }}>−</button>
            <span className="text-[10px] tracking-[0.18em] uppercase font-bold px-1.5"
                  style={{ color: "#E0F2FE" }}>{speed}/s</span>
            <button onClick={() => setSpeed((s) => Math.min(120, s + 10))}
                    data-testid="teleprompter-faster"
                    className="text-[11px] font-bold px-1.5"
                    style={{ color: "#E0F2FE" }}>+</button>
          </div>
          <button onClick={onClose}
                  data-testid="teleprompter-close"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] tracking-[0.18em] uppercase font-bold transition hover:scale-[1.03]"
                  style={{ background: "#EAF6FC", color: "#E0F2FE" }}>
            <X className="w-3.5 h-3.5"/> Exit
          </button>
        </div>
      </div>

      {/* Scrollable script body — cream on black, MASSIVE type */}
      <div ref={containerRef}
           className="flex-1 overflow-y-auto"
           style={{ background: "#FFFFFF", scrollBehavior: "auto" }}>
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-16 pb-[60vh]">

          <div className="text-[12px] tracking-[0.3em] uppercase font-bold mb-3" style={{ color: "#F39A1F" }}>
            ★ CHAPTER {chapter.n}
          </div>
          <h1 className="font-serif-display leading-[1.05]"
              style={{ color: "#1B1B1B", fontSize: "clamp(34px, 5vw, 64px)", textWrap: "balance" }}>
            {chapter.title}
          </h1>
          <p className="mt-4 italic" style={{ color: "#0F172A", fontSize: "clamp(18px, 2vw, 26px)" }}>
            {chapter.summary}
          </p>

          <div className="mt-8 space-y-6" style={{ color: "#1B1B1B" }}>
            {chapter.body.map((blk, i) => {
              if (blk.type === "p") return (
                <p key={i} style={{ fontSize: "clamp(22px, 2.8vw, 38px)", lineHeight: 1.45 }}>
                  {blk.text}
                </p>
              );
              if (blk.type === "h") return (
                <h2 key={i} className="font-serif-display mt-10 mb-1 leading-[1.1]"
                    style={{ color: "#0F172A", fontSize: "clamp(28px, 3.6vw, 48px)" }}>
                  ★ {blk.text}
                </h2>
              );
              if (blk.type === "list") return (
                <ul key={i} className="space-y-3 pl-2">
                  {blk.items.map((it, j) => (
                    <li key={j} className="flex items-start gap-3"
                        style={{ fontSize: "clamp(20px, 2.4vw, 32px)", lineHeight: 1.45 }}>
                      <span style={{ color: "#F39A1F", fontWeight: 800 }}>•</span>
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              );
              if (blk.type === "list-bold") return (
                <ul key={i} className="space-y-4 pl-2">
                  {blk.items.map(([bold, rest], j) => (
                    <li key={j} className="flex items-start gap-3"
                        style={{ fontSize: "clamp(20px, 2.4vw, 32px)", lineHeight: 1.45 }}>
                      <span style={{ color: "#F39A1F", fontWeight: 800 }}>★</span>
                      <span><strong style={{ color: "#0F172A" }}>{bold} —</strong> {rest}</span>
                    </li>
                  ))}
                </ul>
              );
              if (blk.type === "callout") return (
                <div key={i} className="rounded-xl px-5 py-4 italic"
                     style={{ background: "#EAF6FC", border: "2px dashed #F39A1F", fontSize: "clamp(18px, 2.2vw, 28px)", lineHeight: 1.5, color: "#0F172A" }}>
                  {blk.text}
                </div>
              );
              return null;
            })}
          </div>

          <div className="mt-16 pt-6 border-t-2 text-center"
               style={{ borderColor: "#F39A1F44" }}>
            <div className="text-[12px] tracking-[0.3em] uppercase font-bold" style={{ color: "#F39A1F" }}>
              ★ END OF LESSON {String(chapter.n).padStart(2, "0")} SCRIPT
            </div>
            <div className="mt-2 italic" style={{ color: "#0F172A", fontSize: "clamp(16px, 1.8vw, 22px)" }}>
              Hit Exit. Move to the next chapter. Or hit Top + record a retake.
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
